import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateModalProps } from "./index";
import { Button } from "@core/index";
import { useToast } from "@packages/contexts/ToastContext";
import CollaborativeCreateModal from "./CollaborativeCreateModal";

interface CollaborativeLearningCreateModalProps
  extends Omit<CreateModalProps, "onSubmit"> {
  onSubmit: (input: any) => Promise<void>;
}

export const CollaborativeLearningCreateModal: React.FC<
  CollaborativeLearningCreateModalProps
> = ({ onSubmit, initialInput, ...props }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [studentsPerGroup, setStudentsPerGroup] = useState(3);

  const handleSubmit = async (input: any) => {
    // Validate students per group
    if (studentsPerGroup < 2 || studentsPerGroup > 6) {
      addToast({
        title: t("notifications.collaborative.invalidGroupSize"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    // Prepare submission data with collaborative learning-specific fields
    const submissionData = {
      ...input,
      studentsPerGroup,
    };

    await onSubmit(submissionData);
  };

  const CollaborativeSpecificFields = () => (
    <div className="space-y-6">
      {/* Students Per Group Selection */}
      <div className="space-y-2">
        <label className="inline-block text-sm font-medium text-[#8D6B4E] dark:text-[#C8B08D]">
          {t("collaborative.form.studentsPerGroup")}: {studentsPerGroup}
        </label>
        <div className="w-full grid grid-cols-5 gap-3">
          {[2, 3, 4, 5, 6].map((number) => (
            <Button
              key={number}
              type="button"
              onClick={() => setStudentsPerGroup(number)}
              variant="solid"
              size="md"
              rounded="lg"
              fullWidth
              textColor={
                studentsPerGroup === number
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                studentsPerGroup === number
                  ? "bg-gradient-to-r from-[#BFA58A] to-[#A07E5E] shadow-lg"
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
            >
              {number}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <CollaborativeCreateModal
      {...props}
      themeColor="cream"
      onSubmit={handleSubmit}
      initialInput={initialInput}
      after={<CollaborativeSpecificFields />}
    />
  );
};

export default CollaborativeLearningCreateModal;
